// src/pages/admin/manager/formularios/FormulariosMetricasPage.tsx
import { useEffect, useState, Fragment } from "react";
import {
  Stack,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Button,
  Tabs,
  Tab,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Grid,
  Collapse,
  IconButton,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useAuth } from "../../../../context/AuthContext";
import { useIsManager } from "../../../../hooks/useIsManager";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import dayjs, { Dayjs } from "dayjs";
import {
  getFormularioMetrics,
  exportFormularioMetrics,
  getRespuestasIndividuales,
  getFormulario,
  getAsignaciones,
  type FormularioMetrics,
} from "../../../../api/formularios";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#845EC2", "#4D8076", "#F65A83", "#2C73D2"];

export default function FormulariosMetricasPage() {
  const { user, bootstrapped } = useAuth();
  const { isManager, loading: loadingManager } = useIsManager();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation<"translation">();
  const { enqueueSnackbar } = useSnackbar();

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [metricas, setMetricas] = useState<FormularioMetrics | null>(null);
  const [formulario, setFormulario] = useState<any>(null);
  const [respuestas, setRespuestas] = useState<any[]>([]);
  const [expandedUsers, setExpandedUsers] = useState<Record<string, boolean>>({});
  const [asignaciones, setAsignaciones] = useState<any[]>([]);
  
  // Determinar si el formulario tiene usuarios objetivo (tipos especiales)
  const tieneUsuariosObjetivo = formulario?.tipoFormulario && 
    formulario.tipoFormulario !== 'general';
  
  // Agrupar respuestas por usuario objetivo si el formulario tiene tipo especial
  const respuestasPorUsuarioObjetivo = tieneUsuariosObjetivo
    ? respuestas.reduce((acc: Record<number, any[]>, r: any) => {
        const key = r.usuarioObjetivoId || 0;
        if (!acc[key]) acc[key] = [];
        acc[key].push(r);
        return acc;
      }, {})
    : {};
  
  // Obtener lista de usuarios objetivo únicos
  const usuariosObjetivo = tieneUsuariosObjetivo
    ? Array.from(new Set(respuestas.map((r: any) => ({
        id: r.usuarioObjetivoId,
        nombre: r.usuarioObjetivoNombre || `Usuario #${r.usuarioObjetivoId}`,
      })).filter((u: any) => u.id)))
    : [];
  const [filters, setFilters] = useState({
    fechaDesde: null as Dayjs | null,
    fechaHasta: null as Dayjs | null,
    rol: "",
    usuarioId: undefined as number | undefined,
  });

  // Definir funciones antes de los useEffect
  const loadAsignaciones = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getAsignaciones(Number(id));
      setAsignaciones(data);
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Error al cargar asignaciones",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const loadFormulario = async () => {
    if (!id) return;
    try {
      const data = await getFormulario(Number(id));
      setFormulario(data);
    } catch (error) {
      // Error loading formulario
    }
  };

  const loadMetricas = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getFormularioMetrics(Number(id), {
        fechaDesde: filters.fechaDesde?.toISOString(),
        fechaHasta: filters.fechaHasta?.toISOString(),
        rol: filters.rol || undefined,
        usuarioId: filters.usuarioId,
      });
      setMetricas(data);
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Error al cargar métricas",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const loadRespuestas = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getRespuestasIndividuales(Number(id), {
        page: 1,
        limit: 100,
        usuarioId: filters.usuarioId,
        rol: filters.rol || undefined,
      });
      setRespuestas(data.data || []);
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Error al cargar respuestas",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const getUsuarioKey = (r: any) => {
    if (r.usuarioId !== undefined && r.usuarioId !== null) return String(r.usuarioId);
    if (r.usuarioEmail) return String(r.usuarioEmail);
    if (r.usuarioNombre) return String(r.usuarioNombre);
    if (r.id !== undefined && r.id !== null) return String(r.id);
    return `anon-${Math.random().toString(36).slice(2)}`;
  };

  const agruparRespuestasPorUsuario = (items: any[]) => {
    const map = new Map<
      string,
      { key: string; usuarioNombre: string; usuarioEmail?: string | null; respuestas: any[] }
    >();

    items.forEach((r) => {
      const key = getUsuarioKey(r);
      if (!map.has(key)) {
        map.set(key, {
          key,
          usuarioNombre: r.usuarioNombre || r.usuarioId || "-",
          usuarioEmail: r.usuarioEmail || null,
          respuestas: [],
        });
      }
      map.get(key)!.respuestas.push(r);
    });

    return Array.from(map.values());
  };

  const toggleExpanded = (key: string) => {
    setExpandedUsers((prev) => ({ ...prev, [key]: !prev[key] }));
  };


  // Todos los hooks deben estar antes de cualquier return condicional
  useEffect(() => {
    // Componente renderizado
  }, [id]);

  useEffect(() => {
    if (id && bootstrapped && !loadingManager && user && isManager) {
      loadFormulario();
      loadMetricas();
    }
  }, [id, bootstrapped, loadingManager, user, isManager]);

  useEffect(() => {
    if (id && tab === 1 && bootstrapped && !loadingManager && user && isManager) {
      loadRespuestas();
    }
  }, [id, tab, filters, bootstrapped, loadingManager, user, isManager]);

  useEffect(() => {
    if (id && tab === 2 && bootstrapped && !loadingManager && user && isManager) {
      loadAsignaciones();
    }
  }, [id, tab, bootstrapped, loadingManager, user, isManager]);
  
  // Verificación de roles después de todos los hooks
  if (!bootstrapped || loadingManager) {
    return null;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Solo los managers pueden ver métricas
  if (!isManager) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleExport = async () => {
    if (!id) return;
    try {
      const filtros: any = {};
      if (filters.fechaDesde) filtros.fechaDesde = filters.fechaDesde.format("YYYY-MM-DD");
      if (filters.fechaHasta) filtros.fechaHasta = filters.fechaHasta.format("YYYY-MM-DD");
      if (filters.rol) filtros.rol = filters.rol;
      if (filters.usuarioId) filtros.usuarioId = filters.usuarioId;

      const blob = await exportFormularioMetrics(Number(id), filtros);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `formulario-${id}-metricas.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      enqueueSnackbar("Exportación iniciada", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar("Error al exportar", { variant: "error" });
    }
  };

  const tasaPorRolData = metricas?.porRol?.map((t: any) => ({
    rol: t.rol,
    tasa: Number((t.tasaRespuesta || 0).toFixed(1)),
    asignados: t.total || 0,
    completados: t.completados || 0,
  })) || [];

  const distribucionPuntuacionData = metricas?.porPregunta
    ?.filter((m: any) => m.tipo === "puntuacion" && m.distribucion)
    .flatMap((m: any) => {
      // Backend puede devolver array de {valor, cantidad} o Record
      const dist = m.distribucion;
      if (Array.isArray(dist)) {
        return dist.map((item: any) => ({
          pregunta: m.pregunta.substring(0, 30),
          valor: item.valor,
          count: item.cantidad,
        }));
      } else {
        return Object.entries(dist).map(([valor, count]) => ({
          pregunta: m.pregunta.substring(0, 30),
          valor: Number(valor),
          count,
        }));
      }
    }) || [];

  // Agrupar distribución de opciones por pregunta (un gráfico por pregunta)
  const distribucionOpcionesPorPregunta = metricas?.porPregunta
    ?.filter((m: any) => (m.tipo === "seleccion_unica" || m.tipo === "seleccion_multiple") && m.distribucion)
    .map((m: any) => {
      // Backend devuelve array de {opcion, cantidad, opcionId, porcentaje}
      const dist = m.distribucion;
      let opcionesData: Array<{ opcion: string; count: number; porcentaje?: number }> = [];
      
      if (Array.isArray(dist)) {
        opcionesData = dist.map((item: any) => {
          // Asegurar que siempre se muestre el texto de la opción, no el ID
          let textoOpcion = item.opcion && item.opcion.trim() ? item.opcion.trim() : null;
          
          // Si no viene el texto o está vacío, intentar obtenerlo del formulario usando el opcionId
          if (!textoOpcion && item.opcionId && formulario?.preguntas) {
            const pregunta = formulario.preguntas.find((p: any) => p.id === m.preguntaId);
            if (pregunta?.opciones) {
              const opcion = pregunta.opciones.find((o: any) => o.id === item.opcionId);
              if (opcion?.texto && opcion.texto.trim()) {
                textoOpcion = opcion.texto.trim();
              }
            }
          }
          
          // Si aún no tenemos texto, usar un fallback descriptivo
          if (!textoOpcion) {
            textoOpcion = item.opcionId ? `Opción ${item.opcionId}` : "Sin texto";
          }
          
          return {
            opcion: textoOpcion,
            count: item.cantidad || 0,
            porcentaje: item.porcentaje,
          };
        });
      } else {
        // Caso donde dist es un objeto (no debería pasar según el backend, pero lo manejamos)
        opcionesData = Object.entries(dist).map(([key, count]) => {
          // Si la clave es un número (ID), intentar obtener el texto del formulario
          const opcionId = Number(key);
          let textoOpcion = key;
          
          if (!isNaN(opcionId) && formulario?.preguntas) {
            const pregunta = formulario.preguntas.find((p: any) => p.id === m.preguntaId);
            if (pregunta?.opciones) {
              const opcion = pregunta.opciones.find((o: any) => o.id === opcionId);
              if (opcion?.texto) {
                textoOpcion = opcion.texto;
              }
            }
          }
          
          return {
            opcion: textoOpcion,
            count: count as number,
          };
        });
      }
      
      // Calcular porcentajes si no vienen del backend
      const total = m.totalRespuestas || 0;
      const opcionesConPorcentaje = opcionesData.map((opcion) => ({
        ...opcion,
        porcentaje: opcion.porcentaje !== undefined 
          ? opcion.porcentaje 
          : total > 0 
            ? (opcion.count / total) * 100 
            : 0,
      }));
      
      return {
        preguntaId: m.preguntaId,
        pregunta: m.pregunta,
        tipo: m.tipo,
        totalRespuestas: total,
        opciones: opcionesConPorcentaje,
      };
    }) || [];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/admin/manager/formularios")}
          >
            Volver
          </Button>
          <Typography variant="h5">
            {formulario?.titulo || "Métricas del Formulario"}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            startIcon={<FileDownloadIcon />}
            variant="outlined"
            onClick={handleExport}
            disabled={loading}
          >
            Exportar Excel
          </Button>
        </Stack>

        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Métricas" />
          <Tab label="Respuestas Individuales" />
          <Tab label="Asignaciones" />
        </Tabs>

        {tab === 0 && (
          <>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <DateTimePicker
                label="Fecha Desde"
                value={filters.fechaDesde}
                onChange={(v) => setFilters({ ...filters, fechaDesde: v })}
                slotProps={{ textField: { size: "small" } }}
              />
              <DateTimePicker
                label="Fecha Hasta"
                value={filters.fechaHasta}
                onChange={(v) => setFilters({ ...filters, fechaHasta: v })}
                slotProps={{ textField: { size: "small" } }}
              />
              <TextField
                label="Rol"
                size="small"
                value={filters.rol}
                onChange={(e) => setFilters({ ...filters, rol: e.target.value })}
              />
              <Button variant="outlined" onClick={loadMetricas}>
                Aplicar Filtros
              </Button>
            </Stack>

            {loading ? (
              <Stack alignItems="center" p={4}>
                <CircularProgress />
              </Stack>
            ) : metricas ? (
              <Grid container spacing={2}>
                {tieneUsuariosObjetivo && usuariosObjetivo.length > 0 && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
                      <Typography variant="h6" gutterBottom>
                        Formulario de Evaluación por Usuario Objetivo
                      </Typography>
                      <Typography variant="body2">
                        Este formulario evalúa a {usuariosObjetivo.length} usuario{usuariosObjetivo.length !== 1 ? 's' : ''} objetivo.
                        Las respuestas están agrupadas por usuario evaluado.
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Resumen General
                    </Typography>
                    <Typography variant="body1">
                      Total Asignados: {metricas.general?.asignacionesTotales || 0}
                    </Typography>
                    <Typography variant="body1">
                      Total Completados: {metricas.general?.completados || 0}
                    </Typography>
                    <Typography variant="body1">
                      Tasa de Completación: {(metricas.general?.tasaRespuesta || 0).toFixed(1)}%
                    </Typography>
                    {tieneUsuariosObjetivo && usuariosObjetivo.length > 0 && (
                      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                        Usuarios evaluados: {usuariosObjetivo.length}
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                {tasaPorRolData.length > 0 && (
                  <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Tasa de Respuesta por Rol
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={tasaPorRolData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="rol" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="tasa" fill="#8884d8" name="Tasa (%)" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                )}

                {distribucionPuntuacionData.length > 0 && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Distribución de Puntuaciones
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={distribucionPuntuacionData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="valor" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="count" fill="#00C49F" name="Cantidad" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                )}

                {/* Gráficos de distribución de opciones - uno por cada pregunta */}
                {distribucionOpcionesPorPregunta.map((preguntaData: any) => (
                  <Grid item xs={12} md={6} key={preguntaData.preguntaId}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        {preguntaData.pregunta}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                        {preguntaData.tipo === "seleccion_unica" ? "Selección única" : "Selección múltiple"} • 
                        {preguntaData.totalRespuestas} respuesta{preguntaData.totalRespuestas !== 1 ? "s" : ""}
                      </Typography>
                      {preguntaData.opciones.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          {preguntaData.opciones.length <= 5 ? (
                            // PieChart para pocas opciones (más visual)
                            <PieChart>
                              <Pie
                                data={preguntaData.opciones}
                                dataKey="count"
                                nameKey="opcion"
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                label={({ percent = 0 }) => `${(percent * 100).toFixed(0)}%`}
                              >
                                {preguntaData.opciones.map((_: any, index: number) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip 
                                formatter={(value: any, name: string, props: any) => [
                                  `${value} (${props.payload.porcentaje ? props.payload.porcentaje.toFixed(1) : ((value / preguntaData.totalRespuestas) * 100).toFixed(1)}%)`,
                                  name
                                ]}
                              />
                              <Legend 
                                verticalAlign="bottom" 
                                height={36}
                                formatter={(value: string) => value.length > 30 ? `${value.substring(0, 30)}...` : value}
                              />
                            </PieChart>
                          ) : (
                            // BarChart para muchas opciones (más legible)
                            <BarChart data={preguntaData.opciones} layout="vertical">
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" />
                              <YAxis 
                                type="category" 
                                dataKey="opcion" 
                                width={150}
                                tick={{ fontSize: 12 }}
                              />
                              <Tooltip 
                                formatter={(value: any, name: string, props: any) => [
                                  `${value} (${props.payload.porcentaje ? props.payload.porcentaje.toFixed(1) : ((value / preguntaData.totalRespuestas) * 100).toFixed(1)}%)`,
                                  "Cantidad"
                                ]}
                              />
                              <Bar dataKey="count" fill="#8884d8" name="Cantidad">
                                {preguntaData.opciones.map((_: any, index: number) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Bar>
                            </BarChart>
                          )}
                        </ResponsiveContainer>
                      ) : (
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                          No hay respuestas para esta pregunta
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Métricas por Pregunta
                    </Typography>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Pregunta</TableCell>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Total Respuestas</TableCell>
                          <TableCell>Media</TableCell>
                          <TableCell>Mediana</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {metricas.porPregunta?.map((m: any, idx: number) => (
                          <TableRow key={idx}>
                            <TableCell>{m.pregunta}</TableCell>
                            <TableCell>
                              <Chip label={m.tipo} size="small" />
                            </TableCell>
                            <TableCell>{m.totalRespuestas || 0}</TableCell>
                            <TableCell>{m.media?.toFixed(2) || "-"}</TableCell>
                            <TableCell>{m.mediana?.toFixed(2) || "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
              </Grid>
            ) : (
              <Paper sx={{ p: 4 }}>
                <Typography variant="body2" color="text.secondary" align="center">
                  No hay métricas disponibles
                </Typography>
              </Paper>
            )}
          </>
        )}

        {tab === 1 && (
          <>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <DateTimePicker
                label="Fecha Desde"
                value={filters.fechaDesde}
                onChange={(v) => setFilters({ ...filters, fechaDesde: v })}
                slotProps={{ textField: { size: "small" } }}
              />
              <DateTimePicker
                label="Fecha Hasta"
                value={filters.fechaHasta}
                onChange={(v) => setFilters({ ...filters, fechaHasta: v })}
                slotProps={{ textField: { size: "small" } }}
              />
              <Button variant="outlined" onClick={loadRespuestas}>
                Aplicar Filtros
              </Button>
            </Stack>

            {loading ? (
              <Stack alignItems="center" p={4}>
                <CircularProgress />
              </Stack>
            ) : (
              <Paper>
                {tieneUsuariosObjetivo && usuariosObjetivo.length > 0 ? (
                  // Mostrar respuestas agrupadas por usuario objetivo
                  usuariosObjetivo.map((usuarioObjetivo: any) => {
                    const respuestasUsuario = respuestasPorUsuarioObjetivo[usuarioObjetivo.id] || [];
                    const agrupadas = agruparRespuestasPorUsuario(respuestasUsuario);
                    return (
                      <Box key={usuarioObjetivo.id} sx={{ mb: 4 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: '4px 4px 0 0' }}
                        >
                          Evaluación de: {usuarioObjetivo.nombre}
                        </Typography>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell />
                              <TableCell>Evaluador</TableCell>
                              <TableCell>Total Respuestas</TableCell>
                              <TableCell>Última Fecha</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {agrupadas.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4} align="center">
                                  No hay respuestas para este usuario
                                </TableCell>
                              </TableRow>
                            ) : (
                              agrupadas.map((u) => {
                                const open = Boolean(expandedUsers[u.key]);
                                const ultimaFecha = u.respuestas[0]?.createdAt
                                  ? new Date(u.respuestas[0].createdAt).toLocaleDateString("es-ES")
                                  : "-";
                                return (
                                  <Fragment key={u.key}>
                                    <TableRow>
                                      <TableCell>
                                        <IconButton size="small" onClick={() => toggleExpanded(u.key)}>
                                          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                        </IconButton>
                                      </TableCell>
                                      <TableCell>{u.usuarioNombre}</TableCell>
                                      <TableCell>{u.respuestas.length}</TableCell>
                                      <TableCell>{ultimaFecha}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell colSpan={4} sx={{ p: 0 }}>
                                        <Collapse in={open} timeout="auto" unmountOnExit>
                                          <Box sx={{ p: 2 }}>
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow>
                                                  <TableCell>Pregunta</TableCell>
                                                  <TableCell>Respuesta</TableCell>
                                                  <TableCell>Fecha</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {u.respuestas.map((r: any, idx: number) => (
                                                  <TableRow key={idx}>
                                                    <TableCell>{r.preguntaTexto || "-"}</TableCell>
                                                    <TableCell>{r.valorDisplay || "-"}</TableCell>
                                                    <TableCell>
                                                      {r.createdAt
                                                        ? new Date(r.createdAt).toLocaleDateString("es-ES")
                                                        : "-"}
                                                    </TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          </Box>
                                        </Collapse>
                                      </TableCell>
                                    </TableRow>
                                  </Fragment>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </Box>
                    );
                  })
                ) : (
                  // Mostrar respuestas normales (formularios generales)
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell>Usuario</TableCell>
                        <TableCell>Total Respuestas</TableCell>
                        <TableCell>Última Fecha</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {respuestas.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            No hay respuestas
                          </TableCell>
                        </TableRow>
                      ) : (
                        agruparRespuestasPorUsuario(respuestas).map((u) => {
                          const open = Boolean(expandedUsers[u.key]);
                          const ultimaFecha = u.respuestas[0]?.createdAt
                            ? new Date(u.respuestas[0].createdAt).toLocaleDateString("es-ES")
                            : "-";
                          return (
                            <Fragment key={u.key}>
                              <TableRow>
                                <TableCell>
                                  <IconButton size="small" onClick={() => toggleExpanded(u.key)}>
                                    {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                  </IconButton>
                                </TableCell>
                                <TableCell>{u.usuarioNombre}</TableCell>
                                <TableCell>{u.respuestas.length}</TableCell>
                                <TableCell>{ultimaFecha}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell colSpan={4} sx={{ p: 0 }}>
                                  <Collapse in={open} timeout="auto" unmountOnExit>
                                    <Box sx={{ p: 2 }}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Pregunta</TableCell>
                                            <TableCell>Respuesta</TableCell>
                                            <TableCell>Fecha</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {u.respuestas.map((r: any, idx: number) => (
                                            <TableRow key={idx}>
                                              <TableCell>{r.preguntaTexto || "-"}</TableCell>
                                              <TableCell>{r.valorDisplay || "-"}</TableCell>
                                              <TableCell>
                                                {r.createdAt
                                                  ? new Date(r.createdAt).toLocaleDateString("es-ES")
                                                  : "-"}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </Fragment>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                )}
              </Paper>
            )}
          </>
        )}

        {tab === 2 && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Asignaciones del Formulario
            </Typography>
            {loading ? (
              <Stack alignItems="center" py={4}>
                <CircularProgress />
              </Stack>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Usuario</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Rol</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Fecha Asignación</TableCell>
                    <TableCell>Fecha Desasignación</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {asignaciones.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No hay asignaciones para este formulario
                      </TableCell>
                    </TableRow>
                  ) : (
                    asignaciones.map((a: any) => (
                      <TableRow key={a.id}>
                        <TableCell>{a.usuarioNombre || `Usuario #${a.usuarioId}`}</TableCell>
                        <TableCell>{a.usuarioEmail || "-"}</TableCell>
                        <TableCell>{a.rol || "-"}</TableCell>
                        <TableCell>
                          <Chip
                            label={a.activa ? "Activa" : "Desasignada"}
                            size="small"
                            color={a.activa ? "success" : "default"}
                          />
                        </TableCell>
                        <TableCell>
                          {a.asignadoAt
                            ? new Date(a.asignadoAt).toLocaleString("es-ES")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {a.desasignadoAt
                            ? new Date(a.desasignadoAt).toLocaleString("es-ES")
                            : "-"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </Paper>
        )}

      </Stack>
    </LocalizationProvider>
  );
}
